<template>
  <div :class="$style.videoPlayer">
    <audio ref="audio" :class="$style.audio"></audio>
    <CD :class="$style.cd" />
    <div :class="$style.fileName">{{ fileName }}</div>

    <div :class="$style.controller">
      <div :class="$style.playLoading" v-if="audioLoading">
        <i class="el-icon-loading"></i>
      </div>
      <span v-else @click="play" :class="[$style.playControl, playing ? $style.pauseBtn : $style.playBtn]"></span>
      <ProgressBar @onSeek="onSeek" :class="$style.progressBar" />
    </div>
    <VolumeController @toggleMute="toggleMute" @onVolumeChange="onVolumeChange" :class="$style.volumeController" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Emitter from 'element-ui/lib/mixins/emitter'
import CD from './CD'
import ProgressBar from './ProgressBar'
import VolumeController from '@/components/VolumeController'
import { SupportAudioType } from '@/utils/format'
import { errorCodeMap } from '@/utils/errorcodes.js'

let updateTimeStamp = 0
let reloadTimeStamp = 0
// 记录错误重加载次数
let reloadCount = 0

export default {
  name: 'AudioPlayer',
  components: { CD, ProgressBar, VolumeController },
  props: {
    options: Object
  },
  mixins: [Emitter],
  computed: {
    ...mapState(['playing', 'audioLoading', 'duration', 'currentTime', 'muted', 'fileInfo']),
    fileName() {
      let filename = ''
      if (this.fileInfo) {
        filename = this.fileInfo.fname.split('.').slice(0, -1).join('.')
      }
      return filename
    }
  },
  watch: {
    playing() {
      if (this.playing) {
        this.$refs.audio.play()
      } else {
        this.$refs.audio.pause()
      }
    }
  },
  mounted() {
    this.initAudio()
    reloadTimeStamp = 0
    this.$refs.audio.currentTime = 0
  },
  methods: {
    setAudioSrc(audio) {
      let sources = ''
      const stamp = Date.now()
      Array.prototype.forEach.call(SupportAudioType, t => {
        let source = document.createElement('source')
        source.src = `${this.options.url}?stamp=${stamp}`
        source.type = `audio/${t}`
        sources += source.outerHTML
      })
      audio.innerHTML = sources
    },
    initAudio() {
      const audio = this.$refs.audio
      const extName = (this.fileInfo.fname.split('.').reverse()[0]).toLowerCase()

      // 查询文件是否支持播放
      if (!audio.canPlayType(`audio/${extName}`)) {
        this.dispatch('APP', 'showError', { info: { code: errorCodeMap.FILETYPE_NOT_SUPPORT } })
        return
      }
      audio.volume = 0.5

      console.log('=======init', audio, updateTimeStamp)
      // 根据文件后缀添加audioType
      if (this.options && this.options.url) {
        this.setAudioSrc(audio)

        audio.onloadedmetadata = () => {
          this.dispatch('APP', 'dwReporter', {
            page: 'preview',
            operation: 'show'
          })

          // 如果加载成功，不算入reload，归零
          reloadCount = 0
          // 存在 reloadTimeStamp 时，说明是重新加载的音频, 只需要更新当前进度条
          console.log('=======ontimeupdate', audio, reloadTimeStamp)
          if (reloadTimeStamp) {
            this.$refs.audio.currentTime = reloadTimeStamp // 在元数据加载完成后设置
          } else {
            this.$store.commit('SET_MEDIA_DURATION', audio.duration)
            this.$DwManager.recordTotalTime(audio.duration)
            this.$DwManager.reportShowAction('audio', { fileInfo: this.fileInfo, mediaInfo: { duration: audio.duration } })
          }
        }
        audio.ontimeupdate = () => {
          // 记录最新的播放时间
          updateTimeStamp = audio.currentTime
          this.$store.commit('SET_MEDIA_CURRENT_TIME', audio.currentTime)
        }
        audio.onended = () => {
          this.$store.commit('SET_PLAY_STATUS', !this.$refs.audio.paused)
          this.$DwManager.recordPlayTime('ended')

          // 在播放完成以后，立马还原配置（用异步的方式）
          setTimeout(() => {
            reloadCount = 0
            reloadTimeStamp = 0
            updateTimeStamp = 0
          }, 10)
        }
        audio.onerror = (e) => {
          /**
           * 报错后，如果检测到是文件资源加载相关的报错
           * 重新加载音频源，并从记录的播放时间开始播放
           * 记录错误重加载次数，如果相关错误次数大于3次，则不再重新加载
           */
          console.log('=======error', audio.error.code)
          if (audio.error && audio.error.code === 2 && reloadCount < 3) {
            // 发生错误，更新错误时间，避免污染
            reloadTimeStamp = updateTimeStamp
            reloadCount++
            this.mediaReload()
          } else {
            // 遇到奇怪的错误
            console.error('Audio error: ', e)
            this.dispatch('APP', 'showError', { info: { code: errorCodeMap.FILETYPE_NOT_SUPPORT } })
            this.$DwManager.recordInitialPlayStatus(audio.error.code)
          }
        }
        audio.onemptied = () => {
        }

        // dw start
        audio.oncanplaythrough = () => {
          this.$DwManager.measureFileLoaded('end')
        }
        audio.onloadstart = () => {
          this.$DwManager.measureFileLoaded('start')
          this.$DwManager.measureFirstFrameLoaded('start')
        }
        audio.onloadeddata = () => {
          this.$DwManager.measureFirstFrameLoaded('end')
        }
        audio.onwaiting = () => {
          // TODO 缓冲中
          this.$DwManager.measureFirstTimeBuffering('start')
        }
        audio.onplaying = () => {
          this.$DwManager.measureFirstTimeBuffering('end')
        }
        audio.onseeked = () => {
          this.$DwManager.reportUserActiveAction('seeking', 'audio')
        }
        audio.onplay = () => {
          this.$DwManager.reportUserActiveAction('play', 'audio')
          this.$DwManager.recordPlayTime('play')
        }
        audio.onpause = () => {
          this.$DwManager.reportUserActiveAction('pause', 'audio')
          this.$DwManager.recordPlayTime('pause')
        }
        audio.onvolumechange = () => {
          this.$DwManager.reportUserActiveAction('volumechange', 'audio')
        }
        // dw end
      }
    },
    play() {
      if (this.$refs.audio.paused) {
        this.$refs.audio.play()
      } else {
        this.$refs.audio.pause()
      }
      this.$store.commit('SET_PLAY_STATUS', !this.$refs.audio.paused)
      this.dispatch('APP', 'dwReporter', {
        page: 'preview',
        operation: 'play',
        play_time: this.$refs.audio.currentTime.toFixed(2)
      })
    },
    // 重新加载音频资源
    mediaReload() {
      const audio = this.$refs.audio
      console.log('=======error reload', audio)
      this.$store.commit('SET_AUDIO_LOADING', true)
      audio.innerHTML = ''
      this.setAudioSrc(audio)

      // 重新加载音频资源
      audio.load()
      audio.onloadeddata = () => {
        console.log('=======error on reloaded 完成', audio)
        this.$store.commit('SET_AUDIO_LOADING', false)
        this.$refs.audio.play()
      }
    },
    onSeek(val) {
      this.$refs.audio.currentTime = val
    },
    toggleMute() {
      this.$refs.audio.muted = !this.muted
      this.$store.commit('SET_MUTE_STATUS', !this.muted)
      this.muted && this.dispatch('APP', 'dwReporter', {
        page: 'preview',
        operation: 'mute',
        play_time: this.$refs.audio.currentTime.toFixed(2)
      })
    },
    onVolumeChange(volume) {
      this.$refs.audio.volume = volume
      let isMuted = volume <= 0

      this.$refs.audio.muted = isMuted
      this.$store.commit('SET_MUTE_STATUS', isMuted)
      this.$store.commit('SET_VOLUME', volume)
    }
  }
}
</script>

<style lang="scss" module>
.videoPlayer {
  width: 282px;
  height: 419px;
  padding: 22px 30px 30px;
  background: #fff;
  box-shadow: 0 0 13px 0 rgba(81, 88, 113, 0.15);
  border-radius: 3px;
  position: relative;
}

.audio {
  display: none;
}

.cd {
  margin: auto;
}

.fileName {
  margin-top: 0;
  font-size: 20px;
  font-weight: 500;
  color: #3D4757;
  line-height: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.controller {
  display: flex;
  margin-top: 42px;
}

.playLoading {
  width: 58px;
  height: 58px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: #AEB5C0;
}

.volumeController {
  position: absolute;
  top: 16px;
  right: 16px;
}

.playControl {
  width: 58px;
  height: 58px;
  margin-right: 8px;
  align-items: center;
  cursor: pointer;
}

.progressBar {
  flex: 1;
}

.pauseBtn {
  background-image: url('./assets/pause_btn.svg');
}

.playBtn {
  background-image: url('./assets/play_btn.svg');
}

.play {
  cursor: pointer;
  user-select: none;
}
</style>
